// 音檔以靜態 import 取得 URL：Vite 對 `import('*.mp3')` 會額外產生一支「只 export 一個 URL 字串」
// 的 ~700 bytes JS chunk，本檔的 howler 本身已是靜態相依、plugin 也是進場即載入，拆這 14 支 chunk
// 省不到 bytes，卻讓純裝飾性的音效多出 14 個跨網域 module fetch 失敗點；且該失敗會以未捕捉的
// unhandledrejection 逸出到全站 fire-and-forget 的 play() 呼叫端，並誤觸 02.chunk-recovery.client.js
// 的清快取 + 反註冊 SW + 整頁重整（CLIENT-PWA-SSR-1A9）。改為靜態 import 後 URL 直接進 entry bundle，
// 音檔本身由 Howler 取得，取不到時走 onloaderror 靜默降級，不再污染 chunk 錯誤通道。
import dmsBgmSound from 'assets/sounds/background/dms_bgm.mp3'
import loadingSound from 'assets/sounds/background/loading.mp3'
import lobbySound from 'assets/sounds/background/lobby.mp3'
import signUpSound from 'assets/sounds/background/sign_up.mp3'
import clickSound from 'assets/sounds/effect/click.mp3'
import closeSound from 'assets/sounds/effect/close.mp3'
import favoriteClickSound from 'assets/sounds/effect/favorite_click.mp3'
import favoriteUnclickSound from 'assets/sounds/effect/favorite_unclick.mp3'
import jackpotSound from 'assets/sounds/effect/jackpot.mp3'
import lvUpSound from 'assets/sounds/effect/lv_up.mp3'
import mailSound from 'assets/sounds/effect/mail.mp3'
import monsterClickSound from 'assets/sounds/effect/monster_click.mp3'
import monsterUnclickSound from 'assets/sounds/effect/monster_unclick.mp3'
import systemMailSound from 'assets/sounds/effect/system_mail.mp3'
import { Howl, Howler } from 'howler'

// iOS 判斷：涵蓋 iPhone/舊版 iPad，以及升級到 iPadOS 後把 UA 偽裝成 MacIntel 的新版 iPad
const isIOS = () =>
  !!(
    typeof navigator !== 'undefined' &&
    (navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  )

// 迴圈重播延遲：以 macrotask 打破 howler 同步的 _ended → play 呼叫堆疊
const LOOP_RESTART_DELAY = 100
// iOS 忽略 HTMLAudioElement.volume 的寫入，fade 無法實作，改以延遲重試 play() 恢復播放
const IOS_RESUME_DELAY = 500
// 異常長度偵測門檻：正常音軌兩次 end 的間隔就是音檔長度，只有有效長度為 0 的音檔
// 才會以「重播延遲」為間隔連續 end
const RAPID_END_THRESHOLD_MS = LOOP_RESTART_DELAY * 2
const MAX_RAPID_ENDS = 3

// 與 howler fade() 內部相同的正規化，使「是否為退化 fade」的判定與其 diff 計算落在同一組數值上
const clampVolume = (v) => Math.min(Math.max(0, parseFloat(v)), 1)

export default defineNuxtPlugin(() => {
  class SoundControl {
    constructor() {
      this.background = new BackgroundSound()
      this.sound = new Sound()
      console.info('[Sound] initialized.')
    }

    mute(state) {
      this.background.mute(state)
      this.sound.mute(state)
    }

    fade(a, b, c) {
      this.background.fade(a, b, c)
      this.sound.fade(a, b, c)
    }

    volume() {
      return this.background.volume()
    }

    // 使用者音量（0-1）：music 與 sound 聲道各自獨立
    setMusicVolume(v) {
      this.background.setVolume(v)
    }

    setSoundVolume(v) {
      this.sound.setVolume(v)
    }

    getMusicVolume() {
      return this.background.baseVolume
    }

    // 清理所有音效資源，釋放記憶體
    cleanup() {
      this.background.unloadAll()
      this.sound.unloadAll()
    }
  }

  const soundControl = new SoundControl()

  if (import.meta.client) {
    window.soundControl = soundControl

    // 解決 iOS (或現代瀏覽器) 必須由使用者互動才能播放語音的問題
    const unlockAudio = () => {
      // 解鎖時同樣檢查可見性，避免後台點擊觸發播放
      if (!soundControl.background.isMuted && !soundControl.background.playing() && !document.hidden) {
        soundControl.background.play()
      }
      document.removeEventListener('click', unlockAudio)
      document.removeEventListener('touchstart', unlockAudio)
    }

    // 必須使用 { once: true } 或手動 remove，確保只會綁定觸發一次
    document.addEventListener('click', unlockAudio, { once: true })
    document.addEventListener('touchstart', unlockAudio, { once: true })
  }

  return {
    provide: {
      SoundControl: soundControl,
    },
  }
})

// 基礎音效類別
class BaseSound {
  constructor() {
    this.sounds = new Map()
    this.isMuted = true
    // 使用者音量（0-1）；靜音另走 isMuted，不共用 0 音量表示
    this.baseVolume = 1
    this.maxCacheSize = 20 // 限制最大快取數量，避免記憶體累積
    this.accessOrder = [] // 追蹤存取順序，用於 LRU 淘汰
    this.hasWarnedAudioContext = false // AudioContext 不可用只警告一次，避免每次點擊洗版 console
    this._initializeSoundConfigs()
  }

  // 音訊 timer 一律掛在 sound 實例上並集中由此清理。
  // 這兩個 timer 都會在延遲後把播放叫回來，stop() / pause() 沒清掉就會自行復播。
  _clearTimers(sound) {
    if (!sound) return
    if (sound._loopTimer) {
      clearTimeout(sound._loopTimer)
      sound._loopTimer = null
    }
    this._clearResumeTimer(sound)
  }

  _clearResumeTimer(sound) {
    if (!sound) return
    if (sound._iosResumeTimer) {
      clearTimeout(sound._iosResumeTimer)
      sound._iosResumeTimer = null
    }
  }

  // 迴圈重播的有效性判斷；BackgroundSound 另外要求必須是「當前場景」的音軌
  _isActiveSound(key, sound) {
    return this.sounds.get(key) === sound
  }

  // 由子類別實作
  _initializeSoundConfigs() {
    this.configs = {}
  }

  // 更新 LRU 存取順序
  _updateAccessOrder(key) {
    const existingIndex = this.accessOrder.indexOf(key)
    if (existingIndex > -1) {
      this.accessOrder.splice(existingIndex, 1)
    }
    this.accessOrder.push(key)
  }

  // 移除 LRU 存取順序：載入失敗時回滾，維持 accessOrder 與 sounds 的一致性
  _removeAccessOrder(key) {
    const index = this.accessOrder.indexOf(key)
    if (index > -1) {
      this.accessOrder.splice(index, 1)
    }
  }

  // 淘汰最久未使用的音效
  _evictLRU() {
    while (this.sounds.size >= this.maxCacheSize && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift()
      const oldSound = this.sounds.get(oldestKey)
      if (oldSound) {
        this._clearTimers(oldSound)
        oldSound.stop()
        oldSound.unload()
        this.sounds.delete(oldestKey)
        console.log(`[Sound] LRU evicted: ${oldestKey}`)
      }
    }
  }

  async load(key) {
    if (!this.configs[key]) {
      console.warn(`Sound "${key}" not found`)
      return null
    }

    // 更新存取順序
    this._updateAccessOrder(key)

    if (this.sounds.has(key)) {
      return this.sounds.get(key)
    }

    // 檢查快取大小，超過時淘汰最久未使用的
    this._evictLRU()

    const config = this.configs[key]

    // 註：本方法自此至 sounds.set() 之間已無 await（音檔 URL 改為靜態 import），故不再需要
    // 「載入期間 config 被 setSource 換掉」的競態檢查，也不會有併發 load 重複建立 Howl 的問題。
    const options = { ...config.options }
    const isLoop = !!options.loop
    if (isLoop) {
      // RangeError: Maximum call stack size exceeded (CLIENT-PWA-SSR-V3 / CLIENT-PWA-SSR-NQ)
      // 原因：在某些瀏覽器 (如 VivoBrowser) 下，howler 原生 loop 會同步觸發 play -> ended -> play 循環
      // 做法：停用原生 loop，改由 onend 手動觸發非同步 play
      options.loop = false
    }
    // iOS 的 HTML5 Audio volume 唯讀，音量控制無效；改走 Web Audio（html5:false）讓音量/fade 可控。
    // 代價是音檔整份下載解碼進記憶體（bundle 資源同源、CDN 音源已開 CORS）
    if (options.html5 && isIOS()) {
      options.html5 = false
    }

    // iOS Safari 的 AudioContext 被中斷（來電 / 其他 App 搶音訊 / 進背景）後 currentTime 會變成 NaN，
    // Howler 的 Sound.create 會把它餵進 gain.setValueAtTime 而同步拋 TypeError（CLIENT-PWA-SSR-1T1
    // / -1T2）。因 Howl.init 早在 load() 之前就把自己 push 進全域 Howler._howls，拋錯留下的半成品
    // 不在 this.sounds 內、unloadAll() 也摘不到，故寧可在建立前先擋下。只擋 Web Audio 音軌，HTML5
    // 音軌不經 AudioContext、不受影響。
    if (!options.html5 && Howler.ctx && !Number.isFinite(Howler.ctx.currentTime)) {
      if (!this.hasWarnedAudioContext) {
        this.hasWarnedAudioContext = true
        console.warn(`[Sound] AudioContext 不可用（currentTime 非有限值），暫停建立音軌："${key}"`)
      }
      this._removeAccessOrder(key)
      return null
    }
    this.hasWarnedAudioContext = false

    // onloaderror 只涵蓋「音檔取不到」，涵蓋不到建 Web Audio 節點時的同步例外；該例外會沿全站
    // fire-and-forget 的 play() 逸出成 unhandledrejection，故在此一併收斂：與 onloaderror 一致靜默
    // 降級為「無聲」並回傳 null（各呼叫端皆已處理 null），僅留 console 訊號供 triage。音效屬非必要
    // 體驗，任何建構失敗都不值得換掉整站的錯誤通道，故刻意不重新拋出。
    const howlsBeforeCreate = Howler._howls?.length ?? 0
    let sound
    try {
      sound = new Howl({
        src: [config.src],
        preload: true,
        mute: this.isMuted,
        volume: this.baseVolume,
        ...options,
        // 音檔取不到（行動網路 / CDN 抖動）時 Howler 走此 callback，不會拋例外。音效屬非必要的輔助
        // 體驗，此處靜默降級為「無聲」，僅留 console 訊號供 triage，不上報以免產生等量的配額噪音。
        onloaderror: (soundId, error) => {
          console.warn(`[Sound] Failed to load "${key}":`, error)
        },
        onend: () => {
          if (!isLoop) return

          // 以「兩次 end 的間隔」判定裝置回報的長度是否異常（V3 / NQ 那批瀏覽器）：正常音軌的間隔就是
          // 音檔長度，只有有效長度為 0 的音軌會以重播延遲為間隔連續 end。不節流的話遞迴雖已被
          // setTimeout 切斷，仍會變成無上限的固定頻率重播迴圈（CPU / 電量）。
          // 註：不能改用 'play' 事件當基準——該情境下 howler 的 play() 會早退回 _ended，'play' 永不發射。
          const now = Date.now()
          const elapsed = sound._lastEndAt > 0 ? now - sound._lastEndAt : Infinity
          sound._lastEndAt = now
          if (elapsed < RAPID_END_THRESHOLD_MS) {
            sound._rapidEndCount += 1
            // 連續多次即停止重播；該音軌在此裝置靜默是可接受的降級
            if (sound._rapidEndCount >= MAX_RAPID_ENDS) return
          } else {
            sound._rapidEndCount = 0
          }

          // 使用 setTimeout 打破同步呼叫堆疊
          clearTimeout(sound._loopTimer)
          sound._loopTimer = setTimeout(() => {
            sound._loopTimer = null
            // 檢查 sound 是否仍有效 (未被 unload / 淘汰 / 換場景) 且視窗處於焦點狀態
            if (!this._isActiveSound(key, sound)) return
            if (typeof sound.state !== 'function' || sound.state() !== 'loaded') return
            if (document.hidden) return
            sound.play()
          }, LOOP_RESTART_DELAY)
        },
      })
    } catch (error) {
      // Howl.init 在拋錯前已把自己註冊進全域 Howler._howls，該半成品沒有任何路徑能釋放，需手動摘除
      if ((Howler._howls?.length ?? 0) > howlsBeforeCreate) {
        Howler._howls.splice(howlsBeforeCreate)
      }
      this._removeAccessOrder(key)
      console.warn(`[Sound] Failed to create "${key}":`, error)
      return null
    }

    sound._loopTimer = null
    sound._iosResumeTimer = null
    sound._lastEndAt = 0
    sound._rapidEndCount = 0

    this.sounds.set(key, sound)
    return sound
  }

  mute(state) {
    if (typeof state === 'undefined') {
      return this.isMuted
    }
    this.isMuted = state
    for (const sound of this.sounds.values()) {
      sound.mute(state)
    }
    return this.isMuted
  }

  // 設定使用者音量（0-1）並即時套用到所有已載入音軌；未載入的由 load() 的 volume 選項帶入。
  // 漸入中調音量不需特殊處理：Howler 的 volume() 會自動停掉進行中的 fade
  setVolume(v) {
    this.baseVolume = v
    for (const sound of this.sounds.values()) {
      sound.volume(v)
    }
  }

  // howler 的 fade 以「音量每 tick 往目標靠近、到達後才 clearInterval」收尾，故 from 與 to 相等
  // （diff 為 0）時結束條件永遠不成立，_startFadeInterval 起的 setInterval 會永久存活，且 unload()
  // 不會清掉它。該 interval 每次 tick 都對音軌呼叫 volume()，音軌非 loaded 期間這些呼叫全被堆進
  // howler 不會清空的 _queue，待音軌 loaded 後由 _emit ↔ _loadQueue 一次遞迴排空而爆堆疊
  // （CLIENT-PWA-SSR-1S5）。音量相同的 fade 語意上就是「直接設定音量」，故在此收斂，不把退化的
  // fade 交給 howler。
  _fadeSound(sound, from, to, duration) {
    const start = clampVolume(from)
    const target = clampVolume(to)

    // 目標非有限值時 howler 的 volume() 本身即為 no-op（有效區間檢查不通過），此處同樣不動音量
    if (!Number.isFinite(target)) return
    // 起點未知時無從漸變，直接落在呼叫端要求的終點音量
    if (!Number.isFinite(start) || start === target) {
      sound.volume(target)
      return
    }

    sound.fade(start, target, duration)
  }

  fade(from, to, duration) {
    for (const sound of this.sounds.values()) {
      this._fadeSound(sound, from, to, duration)
    }
  }

  stop() {
    for (const sound of this.sounds.values()) {
      this._clearTimers(sound)
      sound.stop()
    }
  }

  // 清理所有音效資源，釋放記憶體
  unloadAll() {
    for (const sound of this.sounds.values()) {
      this._clearTimers(sound)
      sound.stop()
      sound.unload()
    }
    this.sounds.clear()
    this.accessOrder = [] // 同步清理 LRU 順序追蹤
  }
}

// 音效類別（短音效）
class Sound extends BaseSound {
  _initializeSoundConfigs() {
    this.configs = {
      click: { src: clickSound },
      close: { src: closeSound },
      favoriteAdd: { src: favoriteClickSound },
      favoriteRemove: { src: favoriteUnclickSound },
      mailDefault: { src: mailSound },
      mailSystem: { src: systemMailSound },
      levelUp: { src: lvUpSound },
      openDrawer: { src: monsterClickSound },
      closeDrawer: { src: monsterUnclickSound },
    }
  }

  async play(soundType) {
    // 如果已靜音，提前返回，不載入也不播放
    if (this.isMuted) return

    const sound = await this.load(soundType)
    if (!sound) return

    // 只有在視窗處於焦點狀態時才播放短音效
    if (!document.hidden) {
      sound.play()
    }
  }
}

// 背景音樂類別（循環播放）
class BackgroundSound extends BaseSound {
  constructor() {
    super()
    this.scene = ''
    // 進站/重整後首次播放需漸入（規格：音樂從小漸進變大）
    this._fadeInPending = true
  }

  // 首次播放漸入：掛在 load 產生的音軌上，任一場景先播先觸發、僅一次
  async load(key) {
    const sound = await super.load(key)
    if (sound && this._fadeInPending && !sound._fadeInHooked) {
      sound._fadeInHooked = true
      sound.once('play', () => {
        if (!this._fadeInPending) return
        this._fadeInPending = false
        this._fadeSound(sound, 0, this.baseVolume, 2500)
      })
    }
    return sound
  }

  _initializeSoundConfigs() {
    this.configs = {
      index: {
        src: signUpSound,
        options: { loop: true, html5: true },
      },
      lobby: {
        src: lobbySound,
        options: { loop: true, html5: true },
      },
      loading: {
        src: loadingSound,
        options: { loop: true, html5: true },
      },
      diamondShop: {
        src: dmsBgmSound,
        options: { loop: true, html5: true },
      },
      jackpot: {
        src: jackpotSound,
        options: { loop: false, html5: true },
      },
    }
  }

  // 遊戲路由清單：在這些路由下，背景音樂切換預設靜音（jackpot 等特殊事件除外）
  static GAME_ROUTES = ['/game', '/sportsbook', '/luckysport']

  // 特殊事件音效：即使在遊戲內也需要強制播放
  static EVENT_SCENES = ['jackpot']

  _isGameRoute() {
    return typeof window !== 'undefined' && BackgroundSound.GAME_ROUTES.some((path) => window.location.pathname.includes(path))
  }

  async play(scene) {
    if (!scene) {
      // 無參數：重新播放當前場景（用於 iOS unlock 解鎖）
      if (this.isMuted) return

      let sound = this.sounds.get(this.scene)
      if (!sound) {
        sound = await this.load(this.scene)
      }
      // 只有在視窗處於焦點狀態時才播放
      if (sound && !document.hidden) {
        sound.play()
      }
      return
    }

    if (this.scene === scene && this.playing()) return

    const isEventScene = BackgroundSound.EVENT_SCENES.includes(scene)

    // 停止舊場景（連同 pending 的重播 timer，否則舊場景會在切換後 100ms 自行復播成雙軌）
    const currentSound = this.sounds.get(this.scene)
    if (currentSound) {
      this._clearTimers(currentSound)
      currentSound.stop()
    }

    // 更新場景記錄
    this.scene = scene

    if (this.isMuted) return

    // 載入新場景音檔（非同步，可能耗時）
    const sound = await this.load(scene)
    if (!sound) return

    // 競態保護：load 等待期間若 scene 已被別處切換，放棄本次操作
    if (this.scene !== scene) return

    // 最終把關：確認當下是否仍在遊戲路由
    // 原因：load() 等待期間 game.vue 可能已 mounted 並呼叫 fade(1,0)
    // 但因音檔尚未就緒，該 fade 會撲空。此處補救，確保進遊戲後不發聲
    if (this._isGameRoute() && !isEventScene) {
      sound.volume(0)
      return
    }

    sound.volume(this.baseVolume)

    // 只有在視窗處於焦點狀態時才播放背景音樂 (Fixes: HMR noisy start)
    if (!document.hidden) {
      sound.play()
    }
  }

  async setSource(scene, newSource) {
    if (!scene || !newSource || !newSource.src) {
      console.warn('Invalid parameters for setSource')
      return
    }

    // 更新配置
    this.configs[scene] = {
      src: newSource.src,
      options: {
        loop: true,
        html5: true,
        ...newSource.options,
      },
    }

    // 如果該場景的音效已經載入，則需要重新載入
    if (this.sounds.has(scene)) {
      const currentSound = this.sounds.get(scene)
      this._clearTimers(currentSound)
      currentSound.stop()
      currentSound.unload()
      this.sounds.delete(scene)
    }

    // 如果當前正在播放這個場景的音樂，且未靜音，需要重新載入並播放
    if (this.scene === scene && !this.isMuted) {
      const sound = await this.load(scene)
      // load 期間可能已被切到別的場景，或 config 又被 setSource 換掉（此時 load 回傳 null）
      if (!sound || this.scene !== scene) return
      // 重新載入時同樣檢查焦點
      if (!document.hidden) {
        sound.play()
      }
    }
  }

  // 迴圈重播只在「當前場景」有效：this.sounds 是依 scene 快取的，換場景後舊音軌仍留在 Map 中，
  // 只比對 Map 會讓舊場景的 pending 重播照樣執行
  _isActiveSound(key, sound) {
    return this.scene === key && this.sounds.get(key) === sound
  }

  // 覆寫 stop 方法，只停止當前場景
  stop() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      this._clearTimers(sound)
      sound.stop()
    }
  }

  playing() {
    const sound = this.sounds.get(this.scene)
    return sound ? sound.playing() : false
  }

  pause() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      // 兩個 timer 都會在延遲後 play()，不清掉的話暫停後仍會自行復播
      this._clearTimers(sound)
      sound.pause()
    }
  }

  // 覆寫 fade 方法，只 fade 當前場景
  fade(from, to, duration) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      // iOS 特判僅限 HTML5 音軌（volume 唯讀）；iOS 已改走 Web Audio（_html5 為 false）則走正常 fade
      if (isIOS() && sound._html5) {
        if (to === 0) {
          // iOS 的 HTML5 Audio 改變 volume 是無效的，所以 fade 沒用，直接 pause() 避免繼續佔用播放
          this._clearTimers(sound)
          if (sound.playing()) sound.pause()
        } else if (to > 0) {
          sound.volume(to)
          // 只重設延遲復播的 timer，不動迴圈 timer（迴圈仍應照常運作）
          this._clearResumeTimer(sound)
          sound._iosResumeTimer = setTimeout(() => {
            sound._iosResumeTimer = null
            if (!sound.playing() && !document.hidden) sound.play()
          }, IOS_RESUME_DELAY)
        }
      } else {
        // 非 iOS 裝置
        if (document.hidden && to > 0) {
          // 關鍵修正：如果標籤頁在後台且嘗試調大音量 (fade in)
          // 為了避免 howler.js 的 fade 自動啟動聲音，我們強制靜音並暫停
          sound.volume(0)
          if (sound.playing()) sound.pause()
          return
        }

        this._fadeSound(sound, from, to, duration)

        // 解決離開遊戲後背景音樂沒恢復的問題：如果目標音量 > 0，確保音樂正在播放且在前台
        if (to > 0 && !sound.playing() && !document.hidden) {
          sound.play()
        }
      }
    }
  }

  volume(val) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      if (typeof val === 'number') {
        sound.volume(val)
      }
      return sound.volume()
    }
    return 0
  }
}
